import { AxiosRequestConfig } from 'axios';
import axios from './axios';

type GenerateImageResponse = {
	success: boolean;
	message: string;
	runId?: string;
};

/**
 * Generate an AI image from a prompt.
 * Follows existing service layer patterns (axios instance + AxiosRequestConfig).
 *
 * Required headers:
 * - Authorization: Bearer <idToken> (set globally via setUserIdToken)
 * - x-workspace-id: <workspaceId>
 */

export const IMAGE_GENERATION_ENDPOINTS = {
	cover: '/ai/image', // Cover generation (uses coverGeneration AI credits)
	image: '/ai/generate-image', // General image generation (uses imageGeneration AI credits)
} as const;

/**
 * Extract string literal union type from endpoint keys: 'cover' | 'image'
 * TypeScript infers this automatically from the const object
 */
export type ImageGenerationType = keyof typeof IMAGE_GENERATION_ENDPOINTS;

/**
 * Extract string literal union type from endpoint values: '/ai/image' | '/ai/generate-image'
 * Ensures compile-time type safety for endpoint URLs (not generic 'string')
 */
export type ImageGenerationEndpoint = (typeof IMAGE_GENERATION_ENDPOINTS)[ImageGenerationType];

export const generateImage = (
	prompt: string,
	category: string,
	size: string,
	workspaceId: string,
	endpoint: string
): Promise<GenerateImageResponse> => {
	return new Promise(async (resolve, reject) => {
		try {
			console.log('AI generate image payload:', {
				prompt,
				category,
				size,
				workspaceId,
			});
			const resp = await axios.post<GenerateImageResponse>(
				IMAGE_GENERATION_ENDPOINTS[endpoint as ImageGenerationType],
				{
					prompt,
					category,
					size,
				},
				{
					headers: {
						'Content-Type': 'application/json',
						'x-workspace-id': workspaceId,
					},
				}
			);
			const response = resp.data;
			console.log('generateImage response payload:', response);
			resolve(response);
		} catch (error: any) {
			console.log('AI generate image error:', error?.message || error);
			reject(error);
		}
	});
};

type StreamCallbacks = {
	onEvent: (payload: Record<string, unknown>) => void;
	onError?: (error: unknown) => void;
};

export const streamImageProgress = (runId: string, workspaceId: string, { onEvent, onError }: StreamCallbacks) => {
	const controller = new AbortController();
	const baseURL = axios.defaults.baseURL ?? '';
	const trimmedBase = baseURL.endsWith('/') ? baseURL.slice(0, -1) : baseURL;
	const url = `${trimmedBase}/api/stream/image/${runId}`;
	const headers: Record<string, string> = {};
	if (workspaceId) headers['x-workspace-id'] = workspaceId;
	const authHeader = axios.defaults.headers.common?.Authorization;
	if (authHeader) headers['Authorization'] = String(authHeader);

	const connect = async () => {
		try {
			console.log('Connecting to image progress stream:', url);
			const response = await fetch(url, {
				headers,
				signal: controller.signal,
			});

			if (!response.ok || !response.body) {
				throw new Error(`Stream request failed with status ${response.status}`);
			}
			console.log('Image progress stream connected', {
				status: response.status,
				headers: Object.fromEntries(response.headers.entries()),
			});

			const reader = response.body.getReader();
			const decoder = new TextDecoder();
			let buffer = '';

			while (true) {
				const { value, done } = await reader.read();
				if (done) {
					console.log('Image progress stream reader completed for', runId);
					break;
				}

				buffer += decoder.decode(value, { stream: true });
				const segments = buffer.split('\n\n');
				buffer = segments.pop() ?? '';
				for (const segment of segments) {
					const lines = segment.split('\n');
					for (const line of lines) {
						if (line.startsWith('data:')) {
							const data = line.replace(/^data:\s*/, '');
							if (!data) continue;
							try {
								const payload = JSON.parse(data);
								console.log('Stream event payload:', payload);
								onEvent(payload);
							} catch (parseError) {
								console.warn('Unable to parse stream payload:', data, parseError);
							}
						}
					}
				}
			}
		} catch (streamError) {
			if ((streamError as DOMException)?.name === 'AbortError') {
				console.log('Image progress stream aborted intentionally for', runId);
			} else {
				console.error('Image progress stream error:', streamError);
				if (onError) onError(streamError);
			}
		}
		console.log('Image progress stream loop exited for', runId);
	};

	void connect();

	return () => {
		console.log('Closing image progress stream for runId:', runId);
		controller.abort();
	};
};

export const ai_services = {
	/**
	 * Generate a prompt for card cover using backend AI endpoint.
	 * Sends structured data (occasion, customMessage, colorPalette, fontStyle, layoutStyle, aspectRatio, etc.).
	 * Returns the prompt string streamed back from server.
	 */
	generatePrompt: (payload: {
		occasion: string;
		customMessage?: string;
		colorPalette: string[];
		fontStyle: string;
		layoutStyle: string;
		aspectRatio?: string;
		fontStyleDescription?: string;
		layoutStyleDescription?: string;
		aspectRatioDescription?: string;
	}): Promise<string> => {
		return new Promise(async (resolve, reject) => {
			try {
				const config: AxiosRequestConfig = {
					method: 'post',
					url: '/ai/chat',
					data: payload,
					responseType: 'text', // backend streams text/plain
				};
				const resp = await axios(config);
				resolve(resp.data as string);
			} catch (error: any) {
				reject(error);
			}
		});
	},

	/**
	 * Start image generation and stream progress/events.
	 * Mirrors the streaming approach used in `streamImageProgress`.
	 *
	 * Example usage:
	 *   const { runId, cancel } = await ai_services.generateImage({
	 *     prompt: 'text or { prompt: string }',
	 *     category: 'general',
	 *     size: '1024x1024',
	 *     workspaceId,
	 *     onEvent: (evt) => { ...handle events... },
	 *     onError: (err) => { ...handle error... },
	 *   });
	 */
	generateImage: (payload: {
		prompt: string | { prompt: string };
		category?: string;
		size?: '1024x1024' | '1536x1024' | '1024x1536';
		quality?: 'hd' | 'standard' | 'auto';
		workspaceId: string;
		onEvent: (event: Record<string, unknown>) => void;
		onError?: (error: unknown) => void;
	}): Promise<{ runId: string; cancel: () => void }> => {
		return new Promise(async (resolve, reject) => {
			try {
				const promptString = typeof payload.prompt === 'string' ? payload.prompt : payload.prompt?.prompt || '';
				if (!promptString?.trim()) throw new Error('Prompt is required for image generation');
				const category = payload.category?.trim() || 'general';
				const size = '512*512';
				// Kick off generation to receive runId
				const startResp = await generateImage(promptString, category, size, payload.workspaceId, 'cover');
				if (!startResp?.runId) throw new Error(startResp?.message || 'Failed to start image generation');

				// Connect the event stream
				const cancel = streamImageProgress(startResp.runId, payload.workspaceId, {
					onEvent: payload.onEvent,
					onError: payload.onError,
				});

				resolve({ runId: startResp.runId, cancel });
			} catch (error) {
				reject(error);
			}
		});
	},
};
